import { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api/client";
import { EstimateOutput } from "./HolidayDetail";
import { CaseOutput }     from "./CaseDetail";
import { formatDate } from "../lib/format";

export function PrintEstimate() {
  const { id = "" } = useParams();
  const est   = useQuery({ queryKey: ["est-print", id], queryFn: () => api.getEstimate(id) });
  const rates = useQuery({ queryKey: ["rates"], queryFn: api.rateCard });

  useEffect(() => {
    if (est.data && rates.data) setTimeout(() => window.print(), 500);
  }, [est.data, rates.data]);

  const ready = est.data && rates.data;
  const totals = useMemo(() => {
    if (!est.data) return null;
    const e = est.data;
    const r = rates.data?.rates ?? {};
    const dayRate     = (e.rate_day_pence       ?? r.day_hour?.pence       ?? 0) / 100;
    const nightRate   = (e.rate_night_pence     ?? r.night_hour?.pence     ?? 0) / 100;
    const sleepRate   = (e.rate_sleep_in_pence  ?? r.sleep_in?.pence       ?? 0) / 100;
    const expensesRate= (e.rate_expense_per_day_pence ?? r.staff_expense_per_day?.pence ?? 0) / 100;

    let dayHrs = 0, nightHrs = 0, sleeps = 0, dayCost = 0, nightCost = 0, sleepCost = 0;
    for (const d of (e.days ?? [])) {
      dayHrs   += Number(d.incremental_day_hours);
      nightHrs += Number(d.incremental_night_hours);
      sleeps   += Number(d.sleep_in_shifts);
      dayCost  += Number(d.day_cost);
      nightCost += Number(d.night_cost);
      sleepCost += Number(d.sleep_in_cost);
    }
    const daysCount = (e.days ?? []).length;
    const expensesCost = daysCount * expensesRate * (e.staff_count ?? 1);
    const staffingTotal = dayCost + nightCost + sleepCost;
    const total = staffingTotal + expensesCost;
    return { dayHrs, nightHrs, sleeps, dayCost, nightCost, sleepCost, daysCount, expensesCost, staffingTotal, total, dayRate, nightRate, sleepRate, expensesRate };
  }, [est.data, rates.data]);

  if (!ready) return <div className="p-10 text-slate-500">Loading...</div>;
  const e = est.data;

  // Quote validity (from rules table on the server; we fall back to 30 days)
  const validityDays = 30;
  const approvedAt = e.approved_at ? new Date(e.approved_at) : null;
  const expiresAt = approvedAt ? new Date(approvedAt.getTime() + validityDays * 24 * 60 * 60 * 1000) : null;
  const expired = !!expiresAt && Date.now() > expiresAt.getTime();
  const watermarkText = e.status === "approved"
    ? (expired ? "EXPIRED" : "APPROVED")
    : e.status === "draft" ? "DRAFT" : e.status.toUpperCase();
  const watermarkColor = e.status === "approved"
    ? (expired ? "rgba(245, 51, 63, 0.10)" : "rgba(61, 174, 43, 0.08)")
    : "rgba(100, 116, 139, 0.08)";

  return (
    <div className="bg-white p-6 sm:p-10 max-w-[820px] mx-auto print:p-0 relative">
      {/* Diagonal watermark — visible in print and on screen */}
      <div className="pointer-events-none select-none absolute inset-0 flex items-center justify-center" aria-hidden>
        <div style={{
          transform: "rotate(-30deg)",
          fontSize: "120px",
          fontWeight: 800,
          color: watermarkColor,
          whiteSpace: "nowrap",
          letterSpacing: "0.08em",
        }}>
          {watermarkText}
        </div>
      </div>

      {/* Validity callout — hidden in print */}
      {e.status === "approved" && expiresAt && (
        <div className={`no-print mb-4 px-4 py-3 rounded-xl text-sm ${expired ? "bg-liaise-red/10 text-red-700" : "bg-liaise-green/10 text-emerald-800"}`}>
          {expired
            ? <>⚠ This quote expired on {formatDate(expiresAt.toISOString())}. Request a fresh estimate before sending to the funder.</>
            : <>Quote valid until {formatDate(expiresAt.toISOString())} ({validityDays} days from approval).</>}
        </div>
      )}

      {e.type === "case" ? (
        <CaseOutput estimate={e} />
      ) : (
        <EstimateOutput
          status={e.status}
          type={e.type}
          requestNumber={e.request_number}
          pwsInitials={e.pws_initials ?? ""}
          homeName={e.home_name ?? "-"}
          funderName={e.funder_name ?? "-"}
          preparedBy={e.claimant_display_name ?? ""}
          preparedByJobTitle={e.job_title ?? ""}
          startDate={e.start_date?.slice(0, 10) ?? ""}
          endDate={e.end_date?.slice(0, 10) ?? ""}
          staffCount={e.staff_count ?? 1}
          totals={totals!}
          dayRate={totals!.dayRate}
          nightRate={totals!.nightRate}
          sleepRate={totals!.sleepRate}
          expensesRate={totals!.expensesRate}
          adjustments={e.adjustments ?? []}
        />
      )}
    </div>
  );
}
